/**
 * AI Service - Simulates AI-powered features for timesheet management
 * In production, these would call actual ML models/APIs
 */

import type { TimesheetEntry, Project, Timesheet, WorkingPattern, ExtendedWorkingPattern, DayKey } from '@/types/timesheet';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';
import { format, isWeekend, differenceInDays } from 'date-fns';
import { getOrganisationSettings } from './timesheetSettingsService';
import { parseQuickEntryWithValidation as parseQuickEntry, type ParseResult, type Ambiguity, type ProjectMatchContext } from './quickEntryParser';
export type { ParseResult, Ambiguity, ProjectMatchContext } from './quickEntryParser';

export interface AnomalyWarning {
  id: string;
  type: 'excessive-hours' | 'weekend-work' | 'duplicate-entry' | 'closed-project' | 'unusual-pattern' | 'missing-time' | 'overtime-risk' | 'time-and-absence-conflict' | 'contracted-hours-exceeded';
  severity: 'low' | 'medium' | 'high';
  message: string;
  suggestion?: string;
  actionable?: string;
  entryId?: number;
  day?: string;
  autoFixAvailable?: boolean;
  policyCheckType?: string;
  /** Per-day suggested overtime hours. Only present on `contracted-hours-exceeded` warnings.
   *  Keys are day names; value is the suggested OT hours for that day (≥ 0).
   *  Days where no overtime is suggested are omitted. */
  suggestedOvertimeHours?: Partial<Record<DayKey, number>>;
}

export interface ProjectSuggestion {
  projectId: string;
  confidence: number;
  reason: string;
  recentlyUsed?: boolean;
  fromCalendar?: boolean;
  teamPattern?: boolean;
}

export interface AutoFillSuggestion {
  entryId: string;
  projectId: string;
  costCentreId: string;
  description: string;
  hours: Record<string, number>;
  confidence: number;
  reason: string;
}

export interface TimeAllocationPrediction {
  day: string;
  date: Date;
  suggestedHours: number;
  projectId: string;
  reason: string;
  confidence: number;
}


export interface PersonalInsight {
  id: string;
  type: 'productivity' | 'workload' | 'pattern' | 'burnout-risk' | 'efficiency' | 'overtime';
  title: string;
  description: string;
  metric?: string;
  trend?: 'up' | 'down' | 'stable';
  severity?: 'info' | 'warning' | 'critical';
  actionable?: string;
}

// Project matching types and helper functions
interface ProjectMatch {
  project: Project;
  score: number;
  matchReasons: string[];
}


function normalizeText(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9\s]/g, '').trim();
}

function tokenize(text: string): string[] {
  return normalizeText(text).split(/\s+/).filter(token => token.length > 0);
}

function levenshteinDistance(a: string, b: string): number {
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  const matrix: number[][] = [];

  for (let i = 0; i <= b.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= a.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= b.length; i++) {
    for (let j = 1; j <= a.length; j++) {
      if (b.charAt(i - 1) === a.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[b.length][a.length];
}

function findBestProjectMatches(
  input: string,
  projects: Project[],
  context?: ProjectMatchContext
): ProjectMatch[] {
  const normalizedInput = normalizeText(input);
  const inputTokens = tokenize(input);
  const matches: ProjectMatch[] = [];

  for (const project of projects) {
    let score = 0;
    const matchReasons: string[] = [];

    const normalizedCode = normalizeText(project.code);
    const normalizedName = normalizeText(project.name);
    const nameTokens = tokenize(project.name);
    const clientName = project.clientName ? normalizeText(project.clientName) : '';
    const clientTokens = project.clientName ? tokenize(project.clientName) : [];

    // Check for code matches (highest priority)
    if (normalizedInput.includes(normalizedCode) || inputTokens.includes(normalizedCode)) {
      score += 100;
      matchReasons.push('Exact code match');
    } else {
      // Check for normalized code match (without punctuation)
      const codeWithoutPunctuation = project.code.toLowerCase().replace(/[^a-z0-9]/g, '');
      const inputWithoutPunctuation = input.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (inputWithoutPunctuation.includes(codeWithoutPunctuation)) {
        score += 90;
        matchReasons.push('Normalized code match');
      } else {
        // Check for code prefix match
        const codePrefix = normalizedCode.substring(0, Math.min(3, normalizedCode.length));
        if (codePrefix.length >= 3 && inputTokens.some(token => token.startsWith(codePrefix))) {
          score += 80;
          matchReasons.push('Code prefix match');
        }
      }
    }

    // Check for full name match
    if (normalizedInput.includes(normalizedName)) {
      score += 70;
      matchReasons.push('Full name match');
    } else {
      // Check for token matches in project name
      let tokenMatchCount = 0;
      for (const nameToken of nameTokens) {
        if (nameToken.length >= 4) {
          // Exact token match
          if (inputTokens.includes(nameToken)) {
            tokenMatchCount++;
            score += 70;
            matchReasons.push(`Token match: "${nameToken}"`);
          } else {
            // Fuzzy match for longer tokens
            for (const inputToken of inputTokens) {
              if (inputToken.length >= 4) {
                const distance = levenshteinDistance(inputToken, nameToken);
                if (distance <= 2 && distance < nameToken.length * 0.4) {
                  tokenMatchCount++;
                  score += 30;
                  matchReasons.push(`Fuzzy match: "${inputToken}" ~ "${nameToken}"`);
                  break;
                }
              }
            }
          }
        } else if (nameToken.length >= 2) {
          // Short token match (2-3 chars)
          if (inputTokens.includes(nameToken)) {
            tokenMatchCount++;
            score += 50;
            matchReasons.push(`Short token match: "${nameToken}"`);
          }
        }
      }

      // Partial/substring match as fallback
      if (tokenMatchCount === 0) {
        for (const nameToken of nameTokens) {
          if (nameToken.length >= 4) {
            for (const inputToken of inputTokens) {
              if (inputToken.length >= 3 && nameToken.startsWith(inputToken)) {
                score += 40;
                matchReasons.push(`Partial match: "${inputToken}" in "${nameToken}"`);
                break;
              }
            }
          }
        }
      }
    }

    // Check for client name match
    if (clientName) {
      if (normalizedInput.includes(clientName)) {
        score += 60;
        matchReasons.push('Client name match');
      } else {
        for (const clientToken of clientTokens) {
          if (clientToken.length >= 4 && inputTokens.includes(clientToken)) {
            score += 50;
            matchReasons.push(`Client token match: "${clientToken}"`);
            break;
          }
        }
      }
    }

    // Apply user context bonuses
    if (context?.recentProjectIds?.includes(project.id)) {
      score = Math.round(score * 1.5);
      matchReasons.push('Recently used');
    }

    if (context?.favoriteProjectIds?.includes(project.id)) {
      score = Math.round(score * 1.3);
      matchReasons.push('Favorite project');
    }

    if (score > 0) {
      matches.push({ project, score, matchReasons });
    }
  }

  return matches.sort((a, b) => b.score - a.score);
}

class AIService {
  /**
   * Detect anomalies in timesheet entries
   * @param entries - The timesheet entries to check
   * @param weekStart - The start date of the week
   * @param historicalTimesheets - Historical timesheets for pattern analysis
   * @param employeeWorkingPattern - Optional employee-specific working pattern
   * @param organisationSettings - Optional organisation settings (if not provided, falls back to localStorage)
   */
  detectAnomalies(
    entries: TimesheetEntry[],
    weekStart: Date,
    historicalTimesheets: Timesheet[],
    employeeWorkingPattern?: WorkingPattern | ExtendedWorkingPattern,
    organisationSettings?: OrganisationTimesheetSettings
  ): AnomalyWarning[] {
    const warnings: AnomalyWarning[] = [];

    // Get organisation settings (from DB in live mode or localStorage in demo mode)
    const orgSettings = organisationSettings || getOrganisationSettings();
    
    // Get employee's expected hours
    // Priority: employee working pattern > organisation working profile > default 40h/week
    // Check both hoursPerWeek (WorkingPattern) and standardHoursPerWeek (ExtendedWorkingPattern)
    const expectedWeeklyHours = employeeWorkingPattern?.hoursPerWeek 
      || ('standardHoursPerWeek' in (employeeWorkingPattern || {}) ? (employeeWorkingPattern as ExtendedWorkingPattern).standardHoursPerWeek : undefined)
      || orgSettings.workingProfile?.standardHoursPerWeek 
      || 40;
    
    // Get employee's working days
    // Priority: employee working pattern > organisation working profile > default Mon-Fri
    const defaultWorkingDays = {
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: false,
      sunday: false,
    };
    
    // Convert organisation working profile days to working pattern format if available
    // WorkingProfile.workingDays contains WorkingDay objects with isWorkingDay boolean
    const orgWorkingDays = orgSettings.workingProfile?.workingDays ? {
      monday: orgSettings.workingProfile.workingDays.monday?.isWorkingDay ?? true,
      tuesday: orgSettings.workingProfile.workingDays.tuesday?.isWorkingDay ?? true,
      wednesday: orgSettings.workingProfile.workingDays.wednesday?.isWorkingDay ?? true,
      thursday: orgSettings.workingProfile.workingDays.thursday?.isWorkingDay ?? true,
      friday: orgSettings.workingProfile.workingDays.friday?.isWorkingDay ?? true,
      saturday: orgSettings.workingProfile.workingDays.saturday?.isWorkingDay ?? false,
      sunday: orgSettings.workingProfile.workingDays.sunday?.isWorkingDay ?? false,
    } : undefined;
    
    // Normalize employee working days to boolean format
    // ExtendedWorkingPattern has WorkingDay objects, WorkingPattern has booleans
    const normalizeWorkingDays = (days: Record<string, boolean | { isWorkingDay: boolean; expectedHours: number }>): Record<string, boolean> => {
      const result: Record<string, boolean> = {};
      for (const [key, value] of Object.entries(days)) {
        result[key] = typeof value === 'boolean' ? value : value.isWorkingDay;
      }
      return result;
    };
    
    const rawWorkingDays = employeeWorkingPattern?.workingDays || orgWorkingDays || defaultWorkingDays;
    const workingDays = normalizeWorkingDays(rawWorkingDays as Record<string, boolean | { isWorkingDay: boolean; expectedHours: number }>);
    
    // Count working days
    const workingDaysCount = Object.values(workingDays).filter(Boolean).length;
    
    // Calculate expected daily hours based on working pattern
    const expectedDailyHours = workingDaysCount > 0 ? expectedWeeklyHours / workingDaysCount : 8;
    
    // Get organisation hard limits from settings
    const dailyHardLimit = orgSettings.policyLimits?.legalMaxHoursPerDay ?? 13;
    const weeklyHardLimit = orgSettings.policyLimits?.legalMaxHoursPerWeek ?? 48;
    const policySettings = orgSettings.policyViolations;

    // Get monitoring frequency and overage thresholds from policy settings
    // Thresholds are stored as multipliers (e.g. 1.25 = 125% of expected hours)
    // consistent with how WorkingHoursConfiguration.tsx interprets them
    const monitoringFrequency = policySettings?.monitoringFrequency ?? 'week';
    const dailyOverageThreshold = policySettings?.checks?.['daily-overage']?.threshold ?? 1.25;

    // Helper to get per-day expected hours
    // Priority: employee working pattern > org working profile > calculated average
    const getExpectedHoursForDay = (day: string): number => {
      // 1. Check employee working pattern (ExtendedWorkingPattern has WorkingDay objects with expectedHours)
      if (employeeWorkingPattern) {
        const empDays = employeeWorkingPattern.workingDays;
        const empDayConfig = empDays[day as keyof typeof empDays];
        if (empDayConfig && typeof empDayConfig === 'object' && 'expectedHours' in empDayConfig) {
          return empDayConfig.expectedHours;
        }
        // WorkingPattern has dailyHours map
        if (employeeWorkingPattern.dailyHours) {
          const dailyHrs = employeeWorkingPattern.dailyHours[day as keyof typeof employeeWorkingPattern.dailyHours];
          if (dailyHrs !== undefined) {
            return dailyHrs;
          }
        }
        // WorkingPattern with boolean workingDays - calculate from weekly hours / working day count
        if (empDayConfig === true || empDayConfig === false) {
          if (empDayConfig) {
            return expectedDailyHours;
          }
          return 0;
        }
      }
      // 2. Fall back to org working profile
      const wpDays = orgSettings.workingProfile?.workingDays;
      if (wpDays) {
        const dayConfig = wpDays[day as keyof typeof wpDays];
        if (dayConfig?.expectedHours !== undefined) {
          return dayConfig.expectedHours;
        }
      }
      // 3. Fall back to calculated average
      return expectedDailyHours;
    };

    // Calculate daily totals
    const dailyTotals: Record<string, number> = {
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0,
      sunday: 0,
    };

    entries.forEach(entry => {
      Object.entries(entry.hours).forEach(([day, hours]) => {
        dailyTotals[day] += hours;
      });
    });

    // Check for excessive daily hours (based on employee's working pattern)
    Object.entries(dailyTotals).forEach(([day, total]) => {
      if (total === 0) return; // Skip days with no hours
      
      const isWorkingDay = workingDays[day as keyof typeof workingDays];
      const dayName = day.charAt(0).toUpperCase() + day.slice(1);
      const dayExpectedHours = getExpectedHoursForDay(day);
      
      // Hard limit check (applies to all days regardless of working pattern or monitoring frequency)
      // Only check if dailyHardLimit is a positive value (0 means not configured)
      if (dailyHardLimit > 0 && total > dailyHardLimit) {
        warnings.push({
          id: `excessive-${day}`,
          type: 'excessive-hours',
          severity: 'high',
          message: `${dayName}: ${total.toFixed(2)}h exceeds ${dailyHardLimit}h daily limit`,
          suggestion: 'This exceeds organisation policy. Please review and adjust.',
          day,
        });
      }
      // Daily overage check (only when monitoring frequency is 'day')
      // dailyOverageThreshold is a multiplier (e.g. 1.25 = 125% of expected hours)
      else if (monitoringFrequency === 'day' && isWorkingDay && dayExpectedHours > 0 && total > dayExpectedHours * dailyOverageThreshold) {
        warnings.push({
          id: `warning-${day}`,
          type: 'excessive-hours',
          severity: 'medium',
          message: `${dayName}: ${total.toFixed(2)}h exceeds your expected ${dayExpectedHours.toFixed(2)}h by ${(total - dayExpectedHours).toFixed(2)}h`,
          suggestion: 'Ensure overtime is approved and necessary.',
          day,
        });
      }

      // Non-working day check (independent of other checks - always fires for any hours on a non-working day)
      if (!isWorkingDay && total > 0) {
        warnings.push({
          id: `non-working-day-${day}`,
          type: 'weekend-work',
          severity: 'low',
          message: `${dayName}: ${total.toFixed(2)}h logged on non-working day`,
          suggestion: 'Ensure this is approved if outside your normal schedule.',
          day,
        });
      }
    });

    // Check for duplicate entries (same project + cost centre)
    const projectCombos = new Map<string, number>();
    entries.forEach((entry, index) => {
      const key = `${entry.projectId}-${entry.costCentreId}`;
      if (projectCombos.has(key)) {
        warnings.push({
          id: `duplicate-${index}`,
          type: 'duplicate-entry',
          severity: 'low',
          message: `Possible duplicate: Multiple entries for the same project/cost centre`,
          suggestion: 'Consider combining these entries for clarity.',
          entryId: entry.id,
          autoFixAvailable: true,
        });
      }
      projectCombos.set(key, index);
    });

    // Check for missing time (respects monitoring frequency)
    const weekTotal = Object.values(dailyTotals).reduce((sum, h) => sum + h, 0);

    const missingHoursCheckConfig = monitoringFrequency === 'day'
      ? policySettings?.checks?.['daily-missing-hours']
      : policySettings?.checks?.['weekly-missing-hours'];
    const missingHoursThreshold = missingHoursCheckConfig?.threshold ?? 2;

    if (monitoringFrequency === 'day') {
      Object.entries(dailyTotals).forEach(([day, total]) => {
        const dayExpected = getExpectedHoursForDay(day);
        const minimumDailyHours = Math.round((dayExpected - missingHoursThreshold) * 100) / 100;
        if (dayExpected > 0 && total > 0 && total <= minimumDailyHours) {
          const dayName = day.charAt(0).toUpperCase() + day.slice(1);
          warnings.push({
            id: `missing-time-${day}`,
            type: 'missing-time',
            severity: 'medium',
            message: `${dayName}: Only ${total.toFixed(2)}h logged (expected ${dayExpected.toFixed(2)}h)`,
            suggestion: 'Review your calendar and add any missing time entries.',
            day,
          });
        }
      });
    } else {
      const minimumHoursThreshold = Math.round((expectedWeeklyHours - missingHoursThreshold) * 100) / 100;
      if (weekTotal <= minimumHoursThreshold && weekTotal > 0) {
        warnings.push({
          id: 'missing-time',
          type: 'missing-time',
          severity: 'medium',
          message: `Only ${weekTotal.toFixed(2)}h logged (expected ${expectedWeeklyHours}h)`,
          suggestion: 'Review your calendar and add any missing time entries.',
        });
      }
    }

    // Check for unusual patterns based on history
    if (historicalTimesheets.length >= 4) {
      const avgWeeklyHours = historicalTimesheets
        .slice(-4)
        .reduce((sum, ts) => {
          const total = ts.entries.reduce((s, e) => 
            s + Object.values(e.hours).reduce((h, v) => h + v, 0), 0
          );
          return sum + total;
        }, 0) / 4;

      if (weekTotal > avgWeeklyHours * 1.3) {
        warnings.push({
          id: 'unusual-high',
          type: 'unusual-pattern',
          severity: 'medium',
          message: `This week's hours (${weekTotal.toFixed(2)}h) are 30% higher than your 4-week average (${avgWeeklyHours.toFixed(2)}h)`,
          suggestion: 'Verify all entries are accurate. Consider if this workload is sustainable.',
        });
      }
    }

    // Contracted hours exceeded — always fires when total hours exceed the expected weekly hours,
    // independent of overtime settings or policy violation configuration.
    if (weekTotal > expectedWeeklyHours) {
      const overtimeHours = weekTotal - expectedWeeklyHours;

      // Task 3.8 — Build per-day OT suggestions: for each day, max(0, actual − contractedDaily).
      // Only include days where the delta is positive (i.e. where OT was actually worked).
      const suggestedOvertimeHours: Partial<Record<DayKey, number>> = {};
      const DAY_KEYS: DayKey[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      DAY_KEYS.forEach((day) => {
        const actual = dailyTotals[day] ?? 0;
        const contracted = getExpectedHoursForDay(day);
        const delta = Math.round(Math.max(0, actual - contracted) * 100) / 100;
        if (delta > 0) {
          suggestedOvertimeHours[day] = delta;
        }
      });

      warnings.push({
        id: 'contracted-hours-exceeded',
        type: 'contracted-hours-exceeded',
        severity: 'low',
        message: `Total ${weekTotal.toFixed(2)}h exceeds your contracted ${expectedWeeklyHours}h (by ${overtimeHours.toFixed(2)}h)`,
        suggestion: 'You have logged more than your contracted hours this week. Ensure any additional time is approved.',
        actionable: 'Review overtime hours',
        suggestedOvertimeHours: Object.keys(suggestedOvertimeHours).length > 0 ? suggestedOvertimeHours : undefined,
      });
    }

    // Check for overtime risk (based on employee's expected hours and legal limits)
    // The weekly-overage threshold is stored as a multiplier (e.g. 1.10 = 110% of expected hours)
    // consistent with how WorkingHoursConfiguration.tsx interprets it: triggerHours = standardHours * threshold
    const weeklyOverageCheck = policySettings?.checks?.['weekly-overage'];
    const weeklyOverageThreshold = weeklyOverageCheck?.threshold ?? 1.10;
    const weeklyOverageEnabled = weeklyOverageCheck?.enabled ?? true;
    const hasWeeklyViolation = weekTotal > weeklyHardLimit;
    // Only check weekly overage when monitoring frequency is 'week' and the check is enabled
    const hasWeeklyWarning = weeklyOverageEnabled && monitoringFrequency === 'week' && weekTotal >= expectedWeeklyHours * weeklyOverageThreshold;
    
    if (hasWeeklyViolation) {
      // Hard limit violation - always show regardless of monitoring frequency
      warnings.push({
        id: 'overtime-risk',
        type: 'overtime-risk',
        severity: 'high',
        message: `Total ${weekTotal.toFixed(2)}h exceeds legal limit of ${weeklyHardLimit}h`,
        suggestion: 'You must reduce hours before submission. This violates working time regulations.',
        actionable: 'Flag excess hours as overtime',
      });
    } else if (hasWeeklyWarning) {
      warnings.push({
        id: 'overtime-warning',
        type: 'overtime-risk',
        severity: 'medium',
        message: `Total ${weekTotal.toFixed(2)}h exceeds your expected ${expectedWeeklyHours}h (by ${(weekTotal - expectedWeeklyHours).toFixed(2)}h)`,
        suggestion: 'Ensure overtime is approved and necessary.',
        actionable: 'Flag excess hours as overtime',
      });
    }

    // Check for time entries and absences on the same day
    const daysWithAbsence = new Set<string>();
    const daysWithTimeEntry = new Set<string>();
    
    // Identify days with absence entries
    entries.forEach(entry => {
      if (entry.absenceType || entry.sourceIntegration === 'people-hr') {
        Object.entries(entry.hours).forEach(([day, hours]) => {
          if (hours > 0) {
            daysWithAbsence.add(day);
          }
        });
      }
    });
    
    // Identify days with regular time entries (non-absence)
    entries.forEach(entry => {
      if (!entry.absenceType && entry.sourceIntegration !== 'people-hr') {
        Object.entries(entry.hours).forEach(([day, hours]) => {
          if (hours > 0) {
            daysWithTimeEntry.add(day);
          }
        });
      }
    });
    
    // Find days with both time entries and absences
    const conflictDays = Array.from(daysWithAbsence).filter(day => daysWithTimeEntry.has(day));
    
    if (conflictDays.length > 0) {
      const dayNames = conflictDays.map(day => day.charAt(0).toUpperCase() + day.slice(1));
      const daysText = dayNames.length === 1 
        ? dayNames[0] 
        : dayNames.length === 2 
          ? `${dayNames[0]} and ${dayNames[1]}`
          : `${dayNames.slice(0, -1).join(', ')}, and ${dayNames[dayNames.length - 1]}`;
      
      warnings.push({
        id: 'time-and-absence-conflict',
        type: 'time-and-absence-conflict',
        severity: 'medium',
        message: `${daysText}: Both time entry and absence recorded on the same day`,
        suggestion: 'Please verify this is correct. Typically, you should record either time worked or an absence, not both.',
        day: conflictDays[0], // Reference the first conflict day
      });
    }

    // Apply policy violation settings
    return this.applyPolicyViolationSettings(warnings, policySettings, dailyTotals);
  }

  /**
   * Apply policy violation settings to filter/consolidate warnings
   * Supports Options 1, 2, and 3
   */
  private applyPolicyViolationSettings(
    warnings: AnomalyWarning[],
    settings: any, // Full PolicyViolationSettings
    dailyTotals: Record<string, number>
  ): AnomalyWarning[] {
    let filteredWarnings = [...warnings];

    // Option 3: Filter by enabled checks
    const freq = settings.monitoringFrequency ?? 'week';

    if (settings.checks) {
      filteredWarnings = filteredWarnings.filter(w => {
        const checkType = this.mapWarningToCheckType(w, freq);
        if (!checkType) return true; // Keep warnings that don't map to a check type
        if (checkType.includes('legal-')) return true; // Never filter hard limit violations
        
        const checkConfig = settings.checks[checkType];
        return checkConfig?.enabled !== false;
      });

      // Option 3: Apply severity overrides and tag with policy check type
      filteredWarnings = filteredWarnings.map(w => {
        const checkType = this.mapWarningToCheckType(w, freq);
        if (!checkType) return w;
        
        const enriched = { ...w, policyCheckType: checkType };
        const checkConfig = settings.checks[checkType];
        if (checkConfig?.severity) {
          return {
            ...enriched,
            severity: this.mapPolicySeverityToWarningSeverity(checkConfig.severity)
          };
        }
        return enriched;
      });
    }

    // Option 3: Apply display mode
    if (settings.displayMode === 'critical-only') {
      filteredWarnings = filteredWarnings.filter(w => w.severity === 'high');
    } else if (settings.displayMode === 'all') {
      // Show all warnings, skip suppression logic
      return filteredWarnings;
    }

    // Option 3: Filter info alerts
    if (settings.showInfoAlerts === false) {
      filteredWarnings = filteredWarnings.filter(w => w.severity !== 'low');
    }

    // Check if there's a weekly violation
    const hasWeeklyViolation = warnings.some(w => 
      w.type === 'overtime-risk' && w.severity === 'high'
    );

    // Option 1: Suppress daily warnings when weekly limit is exceeded
    if (settings.suppressDailyWhenWeeklyExceeded && hasWeeklyViolation && settings.displayMode !== 'all') {
      filteredWarnings = filteredWarnings.filter(w => 
        // Keep the weekly violation
        (w.type === 'overtime-risk' && w.severity === 'high') ||
        // Remove daily excessive-hours warnings
        (w.type !== 'excessive-hours') ||
        // Keep other types of warnings
        (w.type === 'excessive-hours' && !w.day)
      );
    }

    // Option 1: Consolidate multiple daily warnings
    if (settings.consolidateDailyWarnings && settings.displayMode !== 'all') {
      const dailyExcessiveWarnings = filteredWarnings.filter(w => 
        w.type === 'excessive-hours' && w.day && w.severity !== 'high'
      );

      if (dailyExcessiveWarnings.length > 1) {
        // Remove individual daily warnings
        filteredWarnings = filteredWarnings.filter(w => 
          !(w.type === 'excessive-hours' && w.day && w.severity !== 'high')
        );

        // Calculate total days with warnings
        const daysWithWarnings = dailyExcessiveWarnings.map(w => w.day).filter(Boolean);
        const totalExcessHours = daysWithWarnings.reduce((sum, day) => {
          const total = dailyTotals[day as string] || 0;
          return sum + total;
        }, 0);

        // Add consolidated warning
        filteredWarnings.push({
          id: 'consolidated-daily-warnings',
          type: 'excessive-hours',
          severity: 'medium',
          message: `${daysWithWarnings.length} days have elevated hours (${totalExcessHours.toFixed(2)}h total)`,
          suggestion: `Review ${daysWithWarnings.map(d => d ? d.charAt(0).toUpperCase() + d.slice(1) : '').filter(Boolean).join(', ')} for accuracy and ensure overtime is approved.`,
        });
      }
    }

    return filteredWarnings;
  }

  /**
   * Map AnomalyWarning to PolicyCheckType
   */
  private mapWarningToCheckType(warning: AnomalyWarning, monitoringFrequency: string = 'week'): string | null {
    if (warning.type === 'excessive-hours') {
      if (warning.severity === 'high') {
        return warning.day ? 'legal-daily-maximum' : 'legal-weekly-maximum';
      } else {
        return warning.day ? 'daily-overage' : 'weekly-overage';
      }
    }
    if (warning.type === 'overtime-risk') {
      if (warning.severity === 'high') {
        return 'legal-weekly-maximum';
      }
      return 'weekly-overage';
    }
    if (warning.type === 'missing-time') {
      return monitoringFrequency === 'day' ? 'daily-missing-hours' : 'weekly-missing-hours';
    }
    if (warning.type === 'weekend-work') {
      return monitoringFrequency === 'day' ? 'daily-non-working-day' : 'weekly-non-working-day';
    }
    return null;
  }

  /**
   * Map PolicySeverity to AnomalyWarning severity
   */
  private mapPolicySeverityToWarningSeverity(severity: string): 'low' | 'medium' | 'high' {
    if (severity === 'critical') return 'high';
    if (severity === 'warning') return 'medium';
    return 'low';
  }

  /**
   * Suggest projects based on user patterns and context
   */
  suggestProjects(
    currentUserId: number,
    projects: Project[],
    historicalTimesheets: Timesheet[],
    currentWeekStart: Date
  ): ProjectSuggestion[] {
    const suggestions: ProjectSuggestion[] = [];
    const projectFrequency = new Map<string, number>();
    const recentProjects = new Set<string>();

    // Analyze recent usage (last 4 weeks)
    const recentTimesheets = historicalTimesheets
      .filter(ts => ts.employeeId === currentUserId)
      .slice(-4);

    recentTimesheets.forEach((ts, index) => {
      // Filter to only include timesheet-native entries (exclude external system entries)
      const timesheetNativeEntries = ts.entries.filter(entry => {
        // Exclude entries from scheduler/booking system
        if (entry.isFromSchedule) return false;
        
        // Exclude entries from external integrations (HR systems, etc.)
        if (entry.sourceIntegration) return false;
        
        // Exclude absence entries
        if (entry.absenceType) return false;
        
        // Include all other entries (timesheet-native)
        return true;
      });

      timesheetNativeEntries.forEach(entry => {
        const freq = projectFrequency.get(entry.projectId) || 0;
        // Weight recent weeks more heavily
        const weight = index + 1;
        projectFrequency.set(entry.projectId, freq + weight);

        if (index >= recentTimesheets.length - 2) {
          recentProjects.add(entry.projectId);
        }
      });
    });

    // Create suggestions based on frequency
    Array.from(projectFrequency.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .forEach(([projectId, frequency]) => {
        const project = projects.find(p => p.id === projectId);
        if (project && project.active) {
          const isRecent = recentProjects.has(projectId);
          suggestions.push({
            projectId,
            confidence: Math.min(0.95, frequency / 20),
            reason: isRecent 
              ? 'Used in the last 2 weeks'
              : 'Frequently used in recent months',
            recentlyUsed: isRecent,
          });
        }
      });

    // Add active projects not yet suggested
    projects
      .filter(p => p.active && !projectFrequency.has(p.id))
      .slice(0, 3)
      .forEach(project => {
        suggestions.push({
          projectId: project.id,
          confidence: 0.3,
          reason: 'Active project available',
        });
      });

    return suggestions;
  }

  /**
   * Generate auto-fill suggestions based on patterns
   */
  generateAutoFillSuggestions(
    currentUserId: number,
    historicalTimesheets: Timesheet[],
    currentWeekStart: Date
  ): AutoFillSuggestion[] {
    const suggestions: AutoFillSuggestion[] = [];

    // Get last week's timesheet
    const lastWeekTimesheets = historicalTimesheets
      .filter(ts => ts.employeeId === currentUserId)
      .slice(-1);

    if (lastWeekTimesheets.length === 0) return suggestions;

    const lastWeek = lastWeekTimesheets[0];

    // Filter to only include timesheet-native entries (exclude external system entries)
    const timesheetNativeEntries = lastWeek.entries.filter(entry => {
      // Exclude entries from scheduler/booking system
      if (entry.isFromSchedule) return false;
      
      // Exclude entries from external integrations (HR systems, etc.)
      if (entry.sourceIntegration) return false;
      
      // Exclude absence entries
      if (entry.absenceType) return false;
      
      // Include all other entries (timesheet-native)
      return true;
    });

    // Suggest repeating last week's pattern
    timesheetNativeEntries.forEach((entry, index) => {
      // Calculate average daily hours from last week
      const totalHours = Object.values(entry.hours).reduce((sum, h) => sum + h, 0);
      const avgDaily = totalHours / 5; // Assume 5-day work week

      suggestions.push({
        entryId: `autofill-${index}`,
        projectId: entry.projectId,
        costCentreId: entry.costCentreId,
        description: entry.description,
        hours: {
          monday: avgDaily,
          tuesday: avgDaily,
          wednesday: avgDaily,
          thursday: avgDaily,
          friday: avgDaily,
          saturday: 0,
          sunday: 0,
        },
        confidence: 0.85,
        reason: 'Based on last week\'s pattern',
      });
    });

    return suggestions;
  }

  /**
   * Predict time allocation for the week
   */
  predictTimeAllocation(
    currentUserId: number,
    historicalTimesheets: Timesheet[],
    currentWeekStart: Date,
    projects: Project[]
  ): TimeAllocationPrediction[] {
    const predictions: TimeAllocationPrediction[] = [];

    // Analyze patterns from last 4 weeks
    const recentTimesheets = historicalTimesheets
      .filter(ts => ts.employeeId === currentUserId)
      .slice(-4);

    if (recentTimesheets.length < 2) return predictions;

    // Calculate average daily hours per project
    const projectDailyAvg = new Map<string, Map<string, number>>();

    recentTimesheets.forEach(ts => {
      // Filter to only include timesheet-native entries (exclude external system entries)
      const timesheetNativeEntries = ts.entries.filter(entry => {
        // Exclude entries from scheduler/booking system
        if (entry.isFromSchedule) return false;
        
        // Exclude entries from external integrations (HR systems, etc.)
        if (entry.sourceIntegration) return false;
        
        // Exclude absence entries
        if (entry.absenceType) return false;
        
        // Include all other entries (timesheet-native)
        return true;
      });

      timesheetNativeEntries.forEach(entry => {
        if (!projectDailyAvg.has(entry.projectId)) {
          projectDailyAvg.set(entry.projectId, new Map());
        }
        const dayMap = projectDailyAvg.get(entry.projectId)!;

        Object.entries(entry.hours).forEach(([day, hours]) => {
          const current = dayMap.get(day) || 0;
          dayMap.set(day, current + hours);
        });
      });
    });

    // Generate predictions for each day
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
    days.forEach((day, index) => {
      projectDailyAvg.forEach((dayMap, projectId) => {
        const avgHours = (dayMap.get(day) || 0) / recentTimesheets.length;
        if (avgHours > 0.5) {
          const date = new Date(currentWeekStart);
          date.setDate(date.getDate() + index);

          predictions.push({
            day,
            date,
            suggestedHours: Math.round(avgHours * 2) / 2, // Round to nearest 0.5
            projectId,
            reason: `Based on ${recentTimesheets.length}-week average`,
            confidence: Math.min(0.9, recentTimesheets.length / 4),
          });
        }
      });
    });

    return predictions;
  }

  /**
   * Generate personal insights and analytics
   */
  generatePersonalInsights(
    currentUserId: number,
    historicalTimesheets: Timesheet[]
  ): PersonalInsight[] {
    const insights: PersonalInsight[] = [];

    const userTimesheets = historicalTimesheets.filter(ts => ts.employeeId === currentUserId);
    if (userTimesheets.length < 4) {
      return [{
        id: 'insufficient-data',
        type: 'pattern',
        title: 'Building Your Profile',
        description: 'Keep logging time for a few more weeks to unlock personalized insights and recommendations.',
        severity: 'info',
      }];
    }

    // Sort timesheets by weekEnding date (oldest to newest) to ensure correct "last 4 weeks"
    // Note: DB may return in any order, so we explicitly sort here
    const sortedTimesheets = [...userTimesheets].sort((a, b) => 
      new Date(a.weekEnding).getTime() - new Date(b.weekEnding).getTime()
    );

    // Filter out the current week - only use completed calendar weeks for accurate trends
    // A week is considered "current" if its weekEnding date is >= the start of the current week
    const now = new Date();
    const currentWeekStart = new Date(now);
    currentWeekStart.setHours(0, 0, 0, 0);
    // Go back to the most recent Monday (or Sunday depending on locale, but Monday is standard for business)
    const dayOfWeek = currentWeekStart.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // If Sunday, go back 6 days; otherwise go back to Monday
    currentWeekStart.setDate(currentWeekStart.getDate() - daysToSubtract);

    const completedWeeks = sortedTimesheets.filter(ts => {
      const weekEnd = new Date(ts.weekEnding);
      weekEnd.setHours(0, 0, 0, 0);
      return weekEnd < currentWeekStart; // Only include weeks that ended before the current week started
    });

    // If we don't have enough completed weeks, fall back to using all timesheets
    const timesheetsForAnalysis = completedWeeks.length >= 4 ? completedWeeks : sortedTimesheets;

    // Calculate recent trends - take the 4 most recent completed weeks
    const last4Weeks = timesheetsForAnalysis.slice(-4);
    const weeklyHours = last4Weeks.map(ts => 
      ts.entries.reduce((sum, e) => 
        sum + Object.values(e.hours).reduce((h, v) => h + v, 0), 0
      )
    );

    const avgHours = weeklyHours.reduce((sum, h) => sum + h, 0) / weeklyHours.length;
    const lastWeekHours = weeklyHours[weeklyHours.length - 1];
    const trend = lastWeekHours > avgHours * 1.1 ? 'up' : lastWeekHours < avgHours * 0.9 ? 'down' : 'stable';

    // Workload insight
    insights.push({
      id: 'workload-trend',
      type: 'workload',
      title: 'Weekly Workload Trend',
      description: `Your average weekly hours: ${avgHours.toFixed(1)}h. Last week: ${lastWeekHours.toFixed(1)}h`,
      metric: `${lastWeekHours > avgHours ? '+' : ''}${(lastWeekHours - avgHours).toFixed(1)}h vs average`,
      trend,
      severity: lastWeekHours > 45 ? 'warning' : 'info',
      actionable: lastWeekHours > 45 ? 'Consider discussing workload with your manager' : undefined,
    });

    // Burnout risk detection
    const consecutiveHighWeeks = weeklyHours.filter(h => h > 45).length;
    if (consecutiveHighWeeks >= 2) {
      insights.push({
        id: 'burnout-risk',
        type: 'burnout-risk',
        title: 'Burnout Risk Detected',
        description: `You've logged over 45 hours for ${consecutiveHighWeeks} of the last 4 weeks`,
        severity: 'critical',
        actionable: 'Schedule time off or discuss workload redistribution with your manager',
      });
    }

    // Productivity pattern - Project diversity
    const projectCount = new Set(
      last4Weeks.flatMap(ts => 
        ts.entries
          .filter(entry => !entry.isFromSchedule && !entry.sourceIntegration && !entry.absenceType)
          .map(e => e.projectId)
      )
    ).size;

    insights.push({
      id: 'project-diversity',
      type: 'pattern',
      title: 'Project Diversity',
      description: `You're working on ${projectCount} different projects`,
      severity: projectCount > 5 ? 'warning' : 'info',
      actionable: projectCount > 5 ? 'High project count may impact focus. Consider prioritization.' : undefined,
    });

    // Project concentration insight (per document spec)
    const allEntries = last4Weeks.flatMap(ts => 
      ts.entries.filter(entry => !entry.isFromSchedule && !entry.sourceIntegration && !entry.absenceType)
    );
    const hoursByProject = new Map<string, number>();
    let totalProjectHours = 0;

    allEntries.forEach(entry => {
      const entryHours = Object.values(entry.hours).reduce((a, b) => a + b, 0);
      hoursByProject.set(entry.projectId, (hoursByProject.get(entry.projectId) || 0) + entryHours);
      totalProjectHours += entryHours;
    });

    if (totalProjectHours > 0 && hoursByProject.size > 0) {
      const sortedProjects = Array.from(hoursByProject.entries()).sort((a, b) => b[1] - a[1]);
      const topProjectHours = sortedProjects[0][1];
      const topProjectShare = (topProjectHours / totalProjectHours) * 100;

      insights.push({
        id: 'project-concentration',
        type: 'pattern',
        title: 'Project Concentration',
        description: `Your top project accounts for ${topProjectShare.toFixed(0)}% of your logged hours over the last 4 weeks`,
        metric: `${topProjectShare.toFixed(0)}% on top project`,
        severity: topProjectShare > 70 ? 'warning' : 'info',
        actionable: topProjectShare > 70 ? 'High concentration on a single project may increase risk; consider balancing your workload if appropriate.' : undefined,
      });
    }

    // Workload distribution insight - day of week analysis (per document spec)
    const hoursByDay: Record<string, number> = {
      monday: 0, tuesday: 0, wednesday: 0, thursday: 0, friday: 0, saturday: 0, sunday: 0
    };

    allEntries.forEach(entry => {
      Object.entries(entry.hours).forEach(([day, hours]) => {
        hoursByDay[day] += hours;
      });
    });

    const weekdayHours = hoursByDay.monday + hoursByDay.tuesday + hoursByDay.wednesday + hoursByDay.thursday + hoursByDay.friday;
    const weekendHours = hoursByDay.saturday + hoursByDay.sunday;
    const weekendPercentage = totalProjectHours > 0 ? (weekendHours / totalProjectHours) * 100 : 0;

    if (weekendPercentage > 10) {
      insights.push({
        id: 'workload-distribution',
        type: 'workload',
        title: 'Weekend Work Pattern',
        description: `${weekendPercentage.toFixed(0)}% of your hours are logged on weekends`,
        metric: `${weekendHours.toFixed(1)}h weekend work`,
        severity: weekendPercentage > 20 ? 'warning' : 'info',
        trend: 'stable',
        actionable: weekendPercentage > 20 ? 'Consider redistributing weekend work to weekdays for better work-life balance.' : undefined,
      });
    }

    // Efficiency insight
    const submissionTimes = userTimesheets
      .filter(ts => ts.submittedAt)
      .map(ts => ts.submittedAt!);

    if (submissionTimes.length >= 3) {
      const onTimeSubmissions = submissionTimes.filter(date => {
        // Assuming timesheets should be submitted by Monday
        return date.getDay() <= 1;
      }).length;

      const onTimeRate = (onTimeSubmissions / submissionTimes.length) * 100;

      insights.push({
        id: 'submission-efficiency',
        type: 'efficiency',
        title: 'Submission Timeliness',
        description: `${onTimeRate.toFixed(0)}% of timesheets submitted on time`,
        trend: onTimeRate > 80 ? 'up' : onTimeRate < 50 ? 'down' : 'stable',
        severity: onTimeRate < 50 ? 'warning' : 'info',
        actionable: onTimeRate < 50 ? 'Set a reminder to submit timesheets by end of day Monday' : undefined,
      });
    }

    return insights;
  }

  /**
   * Summarise overtime lines on a single timesheet.
   * Returns a PersonalInsight of type 'overtime', or null when the
   * timesheet has no overtime entries (so callers can skip rendering).
   */
  generateOvertimeInsight(timesheet: Timesheet): PersonalInsight | null {
    // Collect only entries that have been flagged as overtime
    const overtimeEntries = timesheet.entries.filter(e => !!e.overtimeTypeId);
    if (overtimeEntries.length === 0) return null;

    // Total hours across all flagged entries
    const totalOvertimeHours = overtimeEntries.reduce(
      (sum, e) => sum + Object.values(e.hours).reduce((h, v) => h + v, 0),
      0
    );

    // Hours broken down by overtime type name (e.g. "Time & a Half", "Double Time")
    const hoursByType = new Map<string, number>();
    overtimeEntries.forEach(e => {
      const typeName = e.overtimeTypeName ?? 'Unknown Type';
      const entryHours = Object.values(e.hours).reduce((h, v) => h + v, 0);
      hoursByType.set(typeName, (hoursByType.get(typeName) ?? 0) + entryHours);
    });

    // Build a readable breakdown string, e.g. "Time & a Half: 4h, Double Time: 2h"
    const breakdownParts = Array.from(hoursByType.entries())
      .sort((a, b) => b[1] - a[1]) // highest hours first
      .map(([name, hours]) => `${name}: ${hours % 1 === 0 ? hours : hours.toFixed(1)}h`);
    const breakdownText = breakdownParts.join(', ');

    // Severity escalates when overtime is heavy (> 8h flagged on one timesheet)
    const severity: PersonalInsight['severity'] = totalOvertimeHours > 8 ? 'warning' : 'info';

    const entryWord = overtimeEntries.length === 1 ? 'entry' : 'entries';
    const totalDisplay = totalOvertimeHours % 1 === 0
      ? `${totalOvertimeHours}h`
      : `${totalOvertimeHours.toFixed(1)}h`;

    return {
      id: 'overtime-summary',
      type: 'overtime',
      title: 'Overtime This Week',
      description: `${overtimeEntries.length} overtime ${entryWord} totalling ${totalDisplay}. ${breakdownText}.`,
      metric: totalDisplay,
      severity,
      actionable: severity === 'warning'
        ? 'Ensure overtime has been approved by your manager'
        : undefined,
    };
  }

  /**
   * Parse quick entry time input
   * Example: "8 hours on Project Phoenix yesterday"
   */
  parseQuickEntry(
    input: string,
    projects: Project[],
    currentDate: Date = new Date(),
    context?: ProjectMatchContext
  ): Partial<TimesheetEntry> | null {
    const lowerInput = input.toLowerCase();

    // Extract hours
    const hoursMatch = lowerInput.match(/(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|h)/);
    const hours = hoursMatch ? parseFloat(hoursMatch[1]) : null;

    // Extract project using improved matching
    const projectMatches = findBestProjectMatches(input, projects, context);
    const matchedProject = projectMatches.length > 0 ? projectMatches[0].project : null;

    // Extract day
    let targetDay = 'monday';
    if (lowerInput.includes('yesterday')) {
      const yesterday = new Date(currentDate);
      yesterday.setDate(yesterday.getDate() - 1);
      targetDay = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][yesterday.getDay()];
    } else if (lowerInput.includes('today')) {
      targetDay = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][currentDate.getDay()];
    } else {
      // Check for specific day names
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
      for (const day of days) {
        if (lowerInput.includes(day)) {
          targetDay = day;
          break;
        }
      }
    }

    if (!hours || !matchedProject) {
      return null;
    }

    return {
      projectId: matchedProject.id,
      costCentreId: matchedProject.costCentres[0]?.id || '',
      description: input,
      hours: {
        monday: targetDay === 'monday' ? hours : 0,
        tuesday: targetDay === 'tuesday' ? hours : 0,
        wednesday: targetDay === 'wednesday' ? hours : 0,
        thursday: targetDay === 'thursday' ? hours : 0,
        friday: targetDay === 'friday' ? hours : 0,
        saturday: targetDay === 'saturday' ? hours : 0,
        sunday: targetDay === 'sunday' ? hours : 0,
      },
    };
  }

  /**
   * Parse time entry with validation for the new quick entry modal
   * Returns a simplified interface with formatted output
   */
  async parseTimeEntryWithValidation(
    input: string,
    projects: Project[]
  ): Promise<{
    projectId?: string;
    projectName?: string;
    projectCode?: string;
    date?: string;
    dateFormatted?: string;
    hours?: number;
    activityType?: string;
    description?: string;
    confidence: 'high' | 'medium' | 'low';
    warnings?: string[];
    errors?: string[];
    ambiguities?: {
      type: 'project' | 'date' | 'hours';
      message: string;
      options: Array<{ label: string; value: string }>;
    }[];
  }> {
    const currentDate = new Date();
    const result = this.parseQuickEntryWithValidation(input, projects, currentDate);
    
    // Transform to the expected interface
    const response: any = {
      confidence: result.confidence,
      warnings: result.warnings,
      errors: result.errors,
      activityType: result.activityType,
    };

    // Add project info if found
    if (result.entry?.projectId) {
      const project = projects.find(p => p.id === result.entry!.projectId);
      if (project) {
        response.projectId = project.id;
        response.projectName = project.name;
        response.projectCode = project.code;
      }
    }

    // Add hours info
    if (result.entry?.hours) {
      const hoursObj = result.entry.hours;
      const totalHours = Object.values(hoursObj).reduce((sum, h) => sum + h, 0);
      response.hours = totalHours;
      
      // Find which day has hours
      const dayWithHours = Object.entries(hoursObj).find(([_, h]) => h > 0);
      if (dayWithHours) {
        const [day] = dayWithHours;
        // Calculate the actual date
        const dayIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(day);
        const currentDayIndex = currentDate.getDay();
        
        let targetDate = new Date(currentDate);
        const dayDiff = dayIndex - currentDayIndex;
        targetDate.setDate(targetDate.getDate() + dayDiff);
        
        response.date = format(targetDate, 'yyyy-MM-dd');
        response.dateFormatted = format(targetDate, 'EEE, MMM d');
      }
    }

    // Add description
    if (result.entry?.description) {
      response.description = result.entry.description;
    }

    // Transform ambiguities to expected format
    if (result.ambiguities && result.ambiguities.length > 0) {
      response.ambiguities = result.ambiguities.map(amb => ({
        type: amb.type,
        message: amb.message,
        options: (amb.options || []).map(opt => ({
          label: opt,
          value: opt,
        })),
      }));
    }

    return response;
  }

  /**
   * Parse quick entry with validation, confidence levels, and ambiguity detection
   * Delegates to the enhanced quickEntryParser module
   */
  parseQuickEntryWithValidation(
    input: string,
    projects: Project[],
    currentDate: Date = new Date(),
    context?: ProjectMatchContext
  ): ParseResult {
    return parseQuickEntry(input, projects, currentDate, context);
  }

  /**
   * Parse natural language entry (alias for parseQuickEntry)
   * Example: "8 hours on Project Phoenix yesterday"
   */
  parseNaturalLanguageEntry(
    input: string,
    projects: Project[]
  ): Partial<TimesheetEntry> | null {
    return this.parseQuickEntry(input, projects);
  }
}

export const aiService = new AIService();
